import { Building2 } from "lucide-react";
import LandingHeader from "@/components/landing/LandingHeader";
import LandingFooter from "@/components/landing/LandingFooter";
import {
  UseCaseHero,
  UseCaseContext,
  UseCaseProblems,
  UseCaseSolution,
  UseCaseResults,
  UseCaseTestimonial,
  UseCaseCTA,
  RelatedUseCases,
} from "@/components/use-cases";
import { usePageMeta } from "@/hooks/usePageMeta";
import { JsonLd } from "@/components/seo/JsonLd";

const ACCENT_COLOR = "#D4AF37";

const problems = [
  "20-30 chantiers actifs = pilotage impossible",
  "6 corps de metier = difficile de savoir qui coute combien",
  "50% de sous-traitance = factures de partout",
  "Vision consolidee disponible... 3 mois apres",
];

const solutions = [
  {
    title: "Vision consolidee",
    description: "Tous les chantiers sur un seul dashboard",
  },
  {
    title: "Suivi par corps d'etat",
    description: "Marge par specialite sur chaque chantier",
  },
  {
    title: "Suivi sous-traitants",
    description: "Couts par prestataire, comparaison avec previsions",
  },
  {
    title: "Rapports direction",
    description: "PDF pour comites et banques",
  },
  {
    title: "Analyse IA multi-projets",
    description: "Detection des derives sur l'ensemble du portefeuille",
  },
];

const results = [
  {
    metric: "Temps direction sur suivi",
    before: "30h/mois",
    after: "10h/mois",
  },
  {
    metric: "Vision temps reel",
    before: "3 mois de retard",
    after: "Instantanee",
  },
  {
    metric: "Chantiers a risque detectes",
    before: "Apres coup",
    after: "En amont",
  },
];

const MultiCorps = () => {
  usePageMeta({
    title: "Gestion multi-corps de métier BTP — B8ild",
    description: "Pilotez des chantiers multi-lots avec B8ild. Coordination entre corps de métier, suivi financier global et par lot.",
    url: "https://b8ild.fr/use-cases/multi-corps",
  });

  return (
    <div className="min-h-screen bg-[#0A0A0F] overflow-x-hidden">
      <LandingHeader />
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Accueil", item: "https://b8ild.fr/" },
          { "@type": "ListItem", position: 2, name: "Cas d'usage", item: "https://b8ild.fr/use-cases" },
          { "@type": "ListItem", position: 3, name: "Multi-corps", item: "https://b8ild.fr/use-cases/multi-corps" },
        ],
      }} />
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: "Gestion multi-corps de métier BTP — B8ild",
        description: "Pilotez des chantiers multi-lots avec B8ild. Coordination entre corps de métier, suivi financier global et par lot.",
        url: "https://b8ild.fr/use-cases/multi-corps",
        isPartOf: { "@type": "WebSite", name: "B8ild", url: "https://b8ild.fr" },
        inLanguage: "fr",
      }} />

      <UseCaseHero
        badge="Entreprise tous corps d'etat"
        title="Laurent pilote 35 personnes"
        subtitle="et 6 corps de metier. En temps reel."
        description="Decouvrez comment B8ild aide les entreprises multi-corps a avoir une vision consolidee de leur rentabilite."
        icon={Building2}
        accentColor={ACCENT_COLOR}
        personaInitials="LR"
        personaName="Laurent"
        personaRole="PME tous corps d'etat, 35 personnes"
        personaCity="Lille · CA ~4M EUR/an"
      />

      <UseCaseContext
        context="Laurent dirige une entreprise tous corps d'etat depuis 20 ans. Avec 35 personnes reparties sur 6 specialites (maconnerie, electricite, plomberie, peinture, menuiserie, carrelage), il gere 20 a 30 chantiers actifs. La sous-traitance represente 50% de son activite."
        accentColor={ACCENT_COLOR}
      />

      <UseCaseProblems
        problems={problems}
        accentColor={ACCENT_COLOR}
      />

      <UseCaseSolution
        solutions={solutions}
        accentColor={ACCENT_COLOR}
      />

      <UseCaseResults
        results={results}
        accentColor={ACCENT_COLOR}
        period="6 mois"
      />

      <UseCaseTestimonial
        quote="Avant B8ild, je n'avais une vision claire qu'au bilan annuel. Maintenant, je vois en temps reel si un chantier derape. Ma banque apprecie aussi les rapports."
        name="Laurent"
        role="President"
        city="Lille"
        initials="LR"
        accentColor={ACCENT_COLOR}
      />

      {/* Spécificités métier */}
      <section className="py-16 bg-[#0A0A0F]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-6">
              Vos chantiers, on les connaît
            </h2>
            <p className="text-white/60 leading-relaxed">
              Gestion TCE (tous corps d'état), coordination OPC, planning multi-lots, suivi financier par lot et global — quand vous pilotez 6 corps de métier sur 20 chantiers, le suivi Excel ne suffit plus. B8ild vous donne la vision consolidée en temps réel : marge par lot, par chantier, par sous-traitant. Votre banque et vos clients veulent des chiffres ? Vous les avez.
            </p>
          </div>
        </div>
      </section>

      {/* Cas concret */}
      <section className="py-16 bg-[#0A0A0F]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-6">
              Cas concret : Réhabilitation 8 logements, 4 corps de métier, 1 seul qui suit la marge globale
            </h2>
            <div className="space-y-4 text-white/60 leading-relaxed">
              <p>
                L'entreprise Bati+ (15 salariés) a décroché un chantier de réhabilitation : 8 logements dans un immeuble des années 70. Budget total : 420 000€ HT. 4 lots : gros œuvre/maçonnerie (180 000€), plomberie-chauffage (95 000€), électricité (75 000€), peinture-finition (70 000€). Durée : 6 mois. 8 à 12 ouvriers sur site selon les phases. L'OPC (ordonnancement, pilotage, coordination) est géré en interne.
              </p>
              <p>
                Au mois 3, le lot plomberie est en retard de 2 semaines. Le plombier sous-traitant attend des radiateurs en rupture de stock. Conséquence en cascade : les peintres ne peuvent pas intervenir dans 4 logements (murs pas fermés). 2 peintres payés à attendre : 720€/jour de perte. L'électricien ne peut pas poser les prises dans les logements retardés. 1 ouvrier réaffecté en urgence sur un autre chantier — mais le temps de déplacement mange 2h/jour.
              </p>
              <p>
                Impact financier total du retard plomberie : 8 500€ de surcoûts répartis sur 3 lots. La marge globale passe de 16% à 14%. Sur 420 000€, c'est 8 400€ de bénéfice en moins. Avec B8ild, le dirigeant aurait vu dès la semaine 1 du retard que le lot plomberie impactait les 3 autres lots. Il aurait activé un plan B (sous-traitant plombier de remplacement) avant que l'effet domino ne coûte 8 500€.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Questions métier */}
      <section className="py-16 bg-[#08080C]">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-bold text-white mb-8">
              Questions fréquentes — Multi-corps
            </h2>
            <div className="space-y-6">
              {[
                {
                  q: "Comment coordonner plusieurs corps de métier sur un chantier ?",
                  a: "Créez un planning par lot avec les dépendances entre corps de métier. Exemple : la peinture ne commence que quand la plomberie et l'électricité sont terminées dans chaque pièce. Identifiez les tâches critiques (celles qui bloquent les autres). B8ild affiche le planning global et le coût de chaque lot — un retard sur un lot se voit immédiatement sur les autres.",
                },
                {
                  q: "Comment suivre la rentabilité lot par lot ?",
                  a: "Chaque lot a son budget, ses coûts et sa marge. Mais il faut aussi suivre la marge globale du chantier. Un lot à 20% de marge peut masquer un lot à -5%. B8ild consolide automatiquement : vue par lot + vue globale. Vous voyez en un coup d'œil quel lot dérape et combien ça coûte aux autres.",
                },
                {
                  q: "Quel logiciel pour gérer un chantier multi-lots ?",
                  a: "Il faut un outil qui gère les sous-projets (lots) avec consolidation automatique. Excel ne le fait pas sans macro. Les ERP BTP le font mais coûtent 500-2 000€/mois. B8ild gère les lots avec affectation d'équipe par lot, suivi financier par lot, et vue consolidée. À partir de 49€/mois.",
                },
              ].map((faq, i) => (
                <div key={i} className="border-b border-white/[0.06] pb-6">
                  <h3 className="text-lg font-medium text-white mb-3">{faq.q}</h3>
                  <p className="text-white/50 leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <UseCaseCTA accentColor={ACCENT_COLOR} />

      <RelatedUseCases currentSlug="multi-corps" />

      <LandingFooter />
    </div>
  );
};

export default MultiCorps;
